import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Auth } from 'aws-amplify';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { MuiMarkdown } from 'mui-markdown';
import { v4 as uuidv4 } from 'uuid';
import {
  getToolsForRequest,
  type AdminAgentChangedModelRef,
  type AdminAgentClientAction,
  type AdminAgentContext as AgentRouteContext,
  useStore,
} from '@mcn-platform/models';

import { resolveAdminAgentBaseUrl } from '../../config/adminAgentUrl';
import { applyAdminAgentChangedModelRefs } from './applyAdminAgentChangedModelRefs';
import { buildAdminAgentRequestContext } from './buildAdminAgentRequestContext';

export type AgentChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

const drawerWidth = { xs: '100%', sm: 440 };

/** Parse SSE body: text deltas plus optional `changedModels` before `[DONE]`. */
async function collectAssistantResponseFromSse(response: Response): Promise<{
  text: string;
  changedModels: AdminAgentChangedModelRef[];
  clientActions: AdminAgentClientAction[];
}> {
  const text = await response.text();
  let out = '';
  const merged: AdminAgentChangedModelRef[] = [];
  const seen = new Set<string>();
  const actions: AdminAgentClientAction[] = [];
  const blocks = text.split('\n\n');
  for (const block of blocks) {
    const line = block
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l.startsWith('data:'));
    if (!line) continue;
    const payload = line.replace(/^data:\s*/i, '').trim();
    if (payload === '[DONE]') break;
    try {
      const j = JSON.parse(payload) as {
        delta?: string;
        changedModels?: AdminAgentChangedModelRef[];
        clientActions?: AdminAgentClientAction[];
      };
      if (j.delta) out += j.delta;
      if (Array.isArray(j.changedModels)) {
        for (const c of j.changedModels) {
          if (!c?.model) continue;
          const k = `${c.model}:${c.id ?? ''}:${c.organizationID ?? ''}`;
          if (seen.has(k)) continue;
          seen.add(k);
          merged.push({
            model: c.model,
            ...(c.id != null && c.id !== '' ? { id: String(c.id) } : {}),
            ...(c.organizationID != null && c.organizationID !== ''
              ? { organizationID: String(c.organizationID) }
              : {}),
          });
        }
      }
      if (Array.isArray(j.clientActions)) {
        for (const a of j.clientActions) {
          if (a && a.type === 'navigate' && typeof a.path === 'string' && a.path.startsWith('/')) {
            actions.push({
              type: 'navigate',
              path: a.path,
              ...(a.reason != null && String(a.reason).trim() ? { reason: String(a.reason) } : {}),
            });
          }
        }
      }
    } catch {
      // ignore malformed chunk
    }
  }
  return { text: out, changedModels: merged, clientActions: actions };
}

function buildDemoAssistantReply(agentContext: AgentRouteContext, userText: string): string {
  const tools = getToolsForRequest(agentContext, userText);
  const toolLines = tools
    .map((t) => `- **${t.name}** (${t.category}): ${t.description}`)
    .join('\n');
  return [
    '**Admin agent (local demo)**',
    '',
    'No AdminAgent URL applies for this Amplify backend (set `VITE_ADMIN_AGENT_URL` to your Function URL base). Here are the tools that would be available for your current page and message:',
    '',
    toolLines,
    '',
    '---',
    '',
    `**Route context:** \`${JSON.stringify(agentContext)}\``,
  ].join('\n');
}

const AdminAgentDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const store = useStore();
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const agentContext = buildAdminAgentRequestContext(pathname, store);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const userMsg: AgentChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: trimmed,
    };
    const historyForRequest = [...messages, userMsg];
    setMessages(historyForRequest);
    setInput('');
    setError(null);
    setSending(true);

    const baseUrl = resolveAdminAgentBaseUrl();

    try {
      if (!baseUrl) {
        const reply = buildDemoAssistantReply(agentContext, trimmed);
        setMessages((prev) => [
          ...prev,
          { id: uuidv4(), role: 'assistant', content: reply },
        ]);
        return;
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const organizationID = store.currentOrganization?.id;
      const portalID = store.currentPortal?.id;

      const res = await fetch(`${baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream, application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organizationID: organizationID ?? null,
          portalID: portalID ?? null,
          context: agentContext,
          messages: historyForRequest.map(({ role, content }) => ({ role, content })),
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        setError(errText || `Request failed (${res.status})`);
        return;
      }

      const ct = res.headers.get('content-type') || '';
      let assistantText: string;
      let changedModels: AdminAgentChangedModelRef[] = [];
      let clientActions: AdminAgentClientAction[] = [];
      if (ct.includes('text/event-stream')) {
        const r = await collectAssistantResponseFromSse(res);
        assistantText = r.text;
        changedModels = r.changedModels;
        clientActions = r.clientActions;
      } else {
        const raw = await res.text();
        assistantText = raw;
        try {
          const parsed = JSON.parse(raw) as {
            message?: string;
            content?: string;
            changedModels?: AdminAgentChangedModelRef[];
            clientActions?: AdminAgentClientAction[];
          };
          assistantText = parsed.message || parsed.content || raw;
          if (Array.isArray(parsed.changedModels)) {
            changedModels = parsed.changedModels;
          }
          if (Array.isArray(parsed.clientActions)) {
            clientActions = parsed.clientActions.filter(
              (a): a is AdminAgentClientAction =>
                !!a && a.type === 'navigate' && typeof a.path === 'string' && a.path.startsWith('/'),
            );
          }
        } catch {
          // plain text response
        }
      }

      await applyAdminAgentChangedModelRefs(
        store,
        changedModels,
        store.currentOrganization?.id,
      );

      for (const action of clientActions) {
        if (action.type === 'navigate') {
          navigate(action.path);
        }
      }

      setMessages((prev) => [
        ...prev,
        { id: uuidv4(), role: 'assistant', content: assistantText },
      ]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSending(false);
    }
  }, [agentContext, input, messages, sending, store]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56,
          px: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          Admin assistant
        </Typography>
        <IconButton edge="end" onClick={onClose} aria-label="Close assistant">
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Ask a question about the admin app or your current page. With the agentdev
            backend, the deployed AdminAgent Lambda is used automatically. For other
            environments, set{' '}
            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
              VITE_ADMIN_AGENT_URL
            </Typography>{' '}
            to the Function URL base. If no URL applies, you get a local demo that lists
            tools selected for this route.
          </Typography>
        )}
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '100%',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
              color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
            }}
          >
            <MuiMarkdown>{m.content}</MuiMarkdown>
          </Box>
        ))}
        {sending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        <div ref={endRef} />
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={6}
            placeholder="Message the admin assistant…"
            value={input}
            disabled={sending}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            aria-label="Send"
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default observer(AdminAgentDrawer);
